import { formatMetricName } from '../../utils/formatMetric';
import {
  AlarmFieldsFragment,
  AlarmTriggerState,
  NotificationEvent,
} from '../../gql/generated';

/** What an alert watches: the machine's health, or the machine itself. */
export type MachineEventKind = 'health' | 'alarm';

/** One alert from the time it triggered until it resolved. */
export interface MachineEventEpisode {
  id: string;
  alarmId: string | null;
  title: string;
  kind: MachineEventKind;
  start: Date;
  /** Null while the alert is still active, or when its resolve is unknown. */
  end: Date | null;
}

/**
 * One notification in the history. The newest resolve of an alert still in
 * its cooldown period shows as `cooldown` instead of `resolved`.
 */
export interface MachineEventEntry {
  id: string;
  alarmId: string | null;
  title: string;
  kind: MachineEventKind;
  time: Date;
  state: 'triggered' | 'resolved' | 'cooldown';
}

/** EDMS files health signals such as CPU and disk usage under this category. */
const HEALTH_CATEGORY = 'machine-health';

function kindOf(signalCategory?: string | null): MachineEventKind {
  const category = signalCategory?.trim().toLowerCase().replace(/\s+/g, '-');
  return category === HEALTH_CATEGORY ? 'health' : 'alarm';
}

/**
 * What an event is called: the signal that went wrong, which means the same to
 * everyone. A user's own alert name is the fallback.
 */
function eventTitle(event: NotificationEvent): string {
  if (event.signal) return formatMetricName(event.signal);
  return event.alarmName || event.subject || 'Pulse alert';
}

/** Which alert an event belongs to, for pairing its trigger and resolve. */
function alertKey(event: NotificationEvent): string {
  return event.alarmId ?? event.alarmName ?? event.subject ?? event.id;
}

/** EDMS writes a cooldown as D.HH:MM:SS or HH:MM:SS. */
export function parseCooldownMs(value?: string | null): number {
  const match = value?.match(/^(?:(\d+)\.)?(\d+):(\d+):(\d+)/);
  if (!match) return 0;
  const [, days = '0', hours, minutes, seconds] = match;
  return (
    ((Number(days) * 24 + Number(hours)) * 60 + Number(minutes)) * 60_000 +
    Number(seconds) * 1000
  );
}

/** The alerts whose last trigger on this page has no resolve after it. */
function pairNotifications(events: NotificationEvent[]) {
  const oldestFirst = [...events].sort(
    (a, b) => Date.parse(a.timeUtc) - Date.parse(b.timeUtc)
  );
  const open = new Map<string, MachineEventEpisode>();

  for (const event of oldestFirst) {
    const key = alertKey(event);
    const time = new Date(event.timeUtc);

    if (event.alarmTriggerState === AlarmTriggerState.Triggered) {
      // A repeated trigger keeps the episode that is already open.
      if (!open.has(key)) {
        open.set(key, {
          id: event.id,
          alarmId: event.alarmId ?? null,
          title: eventTitle(event),
          kind: kindOf(event.signalCategory),
          start: time,
          end: null,
        });
      }
      continue;
    }

    open.delete(key);
  }

  return { open: [...open.values()] };
}

/**
 * A machine's Pulse alerts: the ones active now, and every trigger and resolve
 * as a history. EDMS keeps `isTriggered` per alert, across all its machines.
 * So an alert on this machine alone is active exactly when EDMS says so, from
 * its last trigger. An alert on several machines falls back to this machine's
 * own trigger and resolve notifications.
 */
export function toEventLog(
  events: NotificationEvent[],
  alarms: AlarmFieldsFragment[],
  now: Date
): { active: MachineEventEpisode[]; history: MachineEventEntry[] } {
  const { open } = pairNotifications(events);
  const alarmsById = new Map(alarms.map((alarm) => [alarm.id, alarm]));

  const active = open.filter((episode) => {
    const alarm = episode.alarmId ? alarmsById.get(episode.alarmId) : null;
    const ownAlarm = alarm && (alarm.machines?.length ?? 0) <= 1;
    // EDMS says an alert on this machine alone ended, though its resolve
    // lies beyond the fetched page.
    return !ownAlarm || alarm.isTriggered;
  });

  // An active alert on this machine alone whose trigger lies beyond the page.
  for (const alarm of alarms) {
    const ownAlarm = (alarm.machines?.length ?? 0) <= 1;
    const shown = active.some((episode) => episode.alarmId === alarm.id);
    if (!ownAlarm || !alarm.isTriggered || shown || !alarm.lastTriggeredAt) {
      continue;
    }
    active.push({
      id: `alarm-${alarm.id}`,
      alarmId: alarm.id,
      title: alarm.signal ? formatMetricName(alarm.signal) : alarm.name,
      kind: kindOf(alarm.signalCategory),
      start: new Date(alarm.lastTriggeredAt),
      end: null,
    });
  }

  const history: MachineEventEntry[] = events
    .map((event) => ({
      id: event.id,
      title: eventTitle(event),
      kind: kindOf(event.signalCategory),
      time: new Date(event.timeUtc),
      state:
        event.alarmTriggerState === AlarmTriggerState.Triggered
          ? ('triggered' as const)
          : ('resolved' as const),
      alarmId: event.alarmId ?? null,
    }))
    .sort((a, b) => b.time.getTime() - a.time.getTime());

  // The cooldown runs from the alert's last trigger, so only its newest
  // resolve can be in it, and none while the alert is active again.
  const marked = new Set(
    active.flatMap((episode) => (episode.alarmId ? [episode.alarmId] : []))
  );
  for (const entry of history) {
    const alarm = entry.alarmId ? alarmsById.get(entry.alarmId) : null;
    if (!alarm?.lastTriggeredAt || marked.has(alarm.id)) continue;
    if (entry.state !== 'resolved') continue;
    marked.add(alarm.id);
    const cooldownEnds =
      Date.parse(alarm.lastTriggeredAt) + parseCooldownMs(alarm.cooldownPeriod);
    if (now.getTime() < cooldownEnds) entry.state = 'cooldown';
  }

  const newestFirst = (a: MachineEventEpisode, b: MachineEventEpisode) =>
    b.start.getTime() - a.start.getTime();

  return {
    active: active.sort(newestFirst),
    history,
  };
}
